import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { DateField } from '@/components/form/fields/date/DateInput';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { MultiTextField } from '@/components/form/fields/text/multi/MultiTextInput';
import { YesNoField } from '@/components/form/fields/yesNo/YesNoInput';

import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { useCompany } from '@/services/api/hooks/companies';
import { useSubmitLeaverRequest } from '@/services/api/hooks/management';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';

const FormFields = z.object({
  customerName: z.string().nonempty(),
  fullName: z.string().nonempty(),
  personalEmail: z.string().email().nonempty(),
  workEmail: z.string().email().nonempty(),
  mobileNumber: z.string().nonempty(),
  devicesToBeCollected: z.array(z.string().nonempty()),
  collectionAddress: z.string().nonempty(),
  canPrintLabel: z.boolean().optional(),
  expediteCollection: z.boolean().optional(),
  targetDate: z.date(),
  notes: z.string().optional()
});

type FormFields = z.infer<typeof FormFields>;

export const LeaversPage: FC = () => {
  const breadcrumbs: NavPage[] = [
    {
      name: 'Management',
      to: ''
    },
    {
      name: 'Leavers',
      to: ''
    }
  ];

  const { partnerId, companyId } = useParams();

  if (!(partnerId && companyId)) throw new Error('Missing params');
  const [company] = useCompany(partnerId, companyId);

  const methods = useForm<FormFields>({
    resolver: zodResolver(FormFields),
    defaultValues: {
      customerName: company.name
    }
  });

  const navigate = useNavigate();
  const routePrefix = useCustomerRoutePrefix();

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const submitRequest = useSubmitLeaverRequest();

  const onSubmit = async (data: FormFields) => {
    setLoading(true);
    setErrorMessage('');

    const result = await submitRequest.mutateAsync({
      customerName: data.customerName,
      fullName: data.fullName,
      personalEmail: data.personalEmail,
      workEmail: data.workEmail,
      mobileNumber: data.mobileNumber,
      devicesToBeCollected: data.devicesToBeCollected,
      collectionAddress: data.collectionAddress,
      targetDate: data.targetDate,
      canPrintLabel: data.canPrintLabel,
      expediteCollection: data.expediteCollection,
      notes: data.notes
    });

    if (result.status === 'Message sent.') {
      navigate(`${routePrefix}/management/success`);
    } else {
      setErrorMessage('Unable to submit request.');
      setLoading(false);
    }
  };

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex flex-col gap-2 items-start">
          <h3 className="text-3xl font-bold">Leavers</h3>
          <p className="font-medium">This is a chargeable service</p>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto p-10">
        <Form methods={methods} onSubmit={onSubmit} className="space-y-5">
          <TextField id="customerName" label="Customer Name" readonly />
          <TextField id="fullName" label="Leaver Full Name" />
          <TextField id="personalEmail" label="Personal Email" />
          <TextField id="workEmail" label="Work Email" />
          <TextField id="mobileNumber" label="Mobile Number" />
          <MultiTextField id="devicesToBeCollected" label="Devices to be collected" placeholder="Add new device name" />
          <TextAreaField
            id="collectionAddress"
            label="Collection Address"
            placeholder="Full address for collection of equipment"
          />
          <YesNoField id="canPrintLabel" label="Can the leaver print a label?" />

          <DateField
            id="targetDate"
            label="Target Collection Date"
            excludeDates={[new Date().toDateString()]}
            excludePastDates={true}
          />
          <TextAreaField id="notes" label="Notes / Special Requirements" />
          {errorMessage && <p className="text-red-500">{errorMessage}</p>}
          <Button variant={ButtonVariant.Primary} type="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </div>
    </>
  );
};
